import { useTranslation } from 'next-i18next/pages';

import { Table } from 'antd';
import type { TFunction } from 'i18next';

import type { ColumnsType } from 'antd/es/table';

import { TranslationNamespace } from '@/i18n';

import { type CalculationResult, formatRub, formatSignedRub } from '../../lib';

import styles from '@/components/TariffCalculatorNew/TariffCalculator.module.scss';

interface BreakdownRow {
	key: string;
	label: string;
	now: number;
	consAfter: number;
	consSaving: number;
	posAfter: number;
	posSaving: number;
	rowType?: 'group' | 'tariff' | 'total';
}

function buildRows(result: CalculationResult, t: TFunction): BreakdownRow[] {
	const groups: BreakdownRow[] = result.groups.map((group, index) => ({
		key: `group-${index}`,
		label: group.label,
		now: group.now,
		consAfter: group.consAfter,
		consSaving: group.consSaving,
		posAfter: group.posAfter,
		posSaving: group.posSaving,
		rowType: 'group',
	}));

	return [
		...groups,
		{
			key: 'tariff',
			label: t('results.tariff'),
			now: 0,
			consAfter: result.tariffYear,
			consSaving: -result.tariffYear,
			posAfter: result.tariffYear,
			posSaving: -result.tariffYear,
			rowType: 'tariff',
		},
		{
			key: 'total',
			label: t('results.total'),
			now: result.currentYear,
			consAfter: result.consAfterYear,
			consSaving: result.consBenefitYear,
			posAfter: result.posAfterYear,
			posSaving: result.posBenefitYear,
			rowType: 'total',
		},
	];
}

function moneyCell(value: number, signed = false) {
	const text = signed ? formatSignedRub(value) : formatRub(value);
	return <span className={signed && value < 0 ? styles.negative : undefined}>{text}</span>;
}

const getColumns = (t: TFunction): ColumnsType<BreakdownRow> => [
	{
		title: t('results.category'),
		dataIndex: 'label',
		key: 'label',
		align: 'left',
	},
	{
		title: t('results.now'),
		dataIndex: 'now',
		key: 'now',
		align: 'right',
		render: (value: number) => moneyCell(value),
	},
	{
		title: t('results.after-conservative'),
		dataIndex: 'consAfter',
		key: 'consAfter',
		align: 'right',
		render: (value: number) => moneyCell(value),
	},
	{
		title: t('results.benefit'),
		dataIndex: 'consSaving',
		key: 'consSaving',
		align: 'right',
		render: (value: number) => moneyCell(value, true),
	},
	{
		title: t('results.after-positive'),
		dataIndex: 'posAfter',
		key: 'posAfter',
		align: 'right',
		render: (value: number) => moneyCell(value),
	},
	{
		title: t('results.benefit'),
		dataIndex: 'posSaving',
		key: 'posSaving',
		align: 'right',
		render: (value: number) => moneyCell(value, true),
	},
];

interface BreakdownTableProps {
	result: CalculationResult;
}

export function BreakdownTable({ result }: BreakdownTableProps) {
	const { t } = useTranslation(TranslationNamespace.Calculator);
	const data = buildRows(result, t);
	const columns = getColumns(t);

	return (
		<div className={styles.breakdownWrap}>
			<h3>{t('results.breakdown')}</h3>
			<div className={styles.tableScroll}>
				<Table<BreakdownRow>
					className={styles.breakdownTable}
					columns={columns}
					dataSource={data}
					pagination={false}
					size="middle"
					scroll={{ x: 930 }}
					rowClassName={(row) => {
						if (row.rowType === 'total') return styles.breakdownTotal;
						if (row.rowType === 'tariff') return styles.breakdownTariff;
						return '';
					}}
				/>
			</div>
		</div>
	);
}
